import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Phone, Building2, ArrowRight, Lock, CheckCircle, Loader, Sparkles } from 'lucide-react';
import { getLogoPath } from '../../shared/utils/assetPaths';
import BrandProtectedImg from '../../shared/components/custom/BrandProtectedImg';
import DemoPwaInstallBanner from './DemoPwaInstallBanner';
import { SAUDI_BRAND } from '../../products/saudi/config/saudiBrandConfig';

const DemoEntry = ({ onSubmit }) => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`onlineDemo.entry.${key}`, opts);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    company: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = p('errors.name_required');
    } else if (formData.name.trim().length < 3) {
      newErrors.name = p('errors.name_min_length');
    }

    if (!formData.phone.trim()) {
      newErrors.phone = p('errors.phone_required');
    } else if (!/^[0-9\s\+\-\(\)]{8,}$/.test(formData.phone.trim())) {
      newErrors.phone = p('errors.phone_invalid');
    }

    if (!formData.company.trim()) {
      newErrors.company = p('errors.company_required');
    } else if (formData.company.trim().length < 2) {
      newErrors.company = p('errors.company_min_length');
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    localStorage.setItem('qawaem_demo_user', JSON.stringify({
      ...formData,
      timestamp: new Date().toISOString()
    }));

    await new Promise(resolve => setTimeout(resolve, 500));

    onSubmit(formData);
  };

  const features = [
    p('feature_pwa'),
    p('feature_demo_data'),
    p('feature_free_access'),
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(29, 71, 196) 50%, rgb(15, 23, 42) 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.25) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(30, 58, 138, 0.2) 0%, transparent 50%)',
        zIndex: 0,
        pointerEvents: 'none'
      }} />

      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(ellipse at center, transparent 20%, rgba(10, 15, 30, 0.4) 80%, rgba(10, 15, 30, 0.6) 100%)',
        zIndex: 0,
        pointerEvents: 'none'
      }} />

      <div style={{
        position: 'relative',
        zIndex: 1,
        maxWidth: '850px',
        width: '100%',
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '20px',
        padding: '2rem 2.5rem',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{
            marginBottom: '1.5rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <BrandProtectedImg
              src={getLogoPath()}
              alt={SAUDI_BRAND.appName}
              style={{
                height: '52px',
                width: 'auto',
                filter: 'drop-shadow(0 4px 24px rgba(0, 0, 0, 0.45))'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />

            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1.5rem',
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(37, 99, 235, 0.1))',
              backdropFilter: 'blur(10px)',
              borderRadius: '8px',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              boxShadow: '0 4px 20px rgba(59, 130, 246, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: '#10b981',
                boxShadow: '0 0 10px #10b981',
                animation: 'pulse 2s ease-in-out infinite'
              }} />
              <span style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#60A5FA',
                textTransform: 'uppercase',
                letterSpacing: '2px'
              }}>
                {p('demo_badge')}
              </span>
            </div>
          </div>

          <DemoPwaInstallBanner compact />

          <h1 style={{
            color: 'white',
            fontSize: '1.5rem',
            fontWeight: '600',
            marginBottom: '0.25rem'
          }}>
            {p('welcome_title')}
          </h1>
          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '0.9rem'
          }}>
            {p('welcome_subtitle')}
          </p>
        </div>

        <form
          method="post"
          action=""
          autoComplete="off"
          onSubmit={handleSubmit}
          style={{ marginBottom: '1.5rem', maxWidth: '650px', margin: '0 auto 1.5rem auto' }}
        >
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="name" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: '500',
              marginBottom: '0.5rem'
            }}>
              <User size={16} />
              {p('name_label')}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              placeholder={p('name_placeholder')}
              value={formData.name}
              onChange={handleChange}
              autoFocus
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: errors.name ? '2px solid #ef4444' : '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '10px',
                color: 'white',
                fontSize: '0.95rem',
                outline: 'none',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(10px)'
              }}
              onFocus={(e) => {
                e.target.style.border = '2px solid #3B82F6';
                e.target.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
              onBlur={(e) => {
                e.target.style.border = errors.name ? '2px solid #ef4444' : '1px solid rgba(255, 255, 255, 0.2)';
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
            />
            {errors.name && (
              <div style={{
                color: '#fca5a5',
                fontSize: '0.75rem',
                marginTop: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}>
                {errors.name}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="phone" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: '500',
              marginBottom: '0.5rem'
            }}>
              <Phone size={16} />
              {p('phone_label')}
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              placeholder={p('phone_placeholder')}
              value={formData.phone}
              onChange={handleChange}
              dir="ltr"
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: errors.phone ? '2px solid #ef4444' : '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '10px',
                color: 'white',
                fontSize: '0.95rem',
                outline: 'none',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(10px)'
              }}
              onFocus={(e) => {
                e.target.style.border = '2px solid #3B82F6';
                e.target.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
              onBlur={(e) => {
                e.target.style.border = errors.phone ? '2px solid #ef4444' : '1px solid rgba(255, 255, 255, 0.2)';
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
            />
            {errors.phone && (
              <div style={{
                color: '#fca5a5',
                fontSize: '0.75rem',
                marginTop: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}>
                {errors.phone}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '1.25rem' }}>
            <label htmlFor="company" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: '500',
              marginBottom: '0.5rem'
            }}>
              <Building2 size={16} />
              {p('company_label')}
            </label>
            <input
              type="text"
              id="company"
              name="company"
              placeholder={p('company_placeholder')}
              value={formData.company}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: errors.company ? '2px solid #ef4444' : '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '10px',
                color: 'white',
                fontSize: '0.95rem',
                outline: 'none',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(10px)'
              }}
              onFocus={(e) => {
                e.target.style.border = '2px solid #3B82F6';
                e.target.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
              onBlur={(e) => {
                e.target.style.border = errors.company ? '2px solid #ef4444' : '1px solid rgba(255, 255, 255, 0.2)';
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
            />
            {errors.company && (
              <div style={{
                color: '#fca5a5',
                fontSize: '0.75rem',
                marginTop: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}>
                {errors.company}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: '100%',
              padding: '0.875rem',
              background: isSubmitting ? 'rgba(59, 130, 246, 0.5)' : 'linear-gradient(135deg, #3B82F6, #2563EB)',
              border: 'none',
              borderRadius: '10px',
              color: 'white',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)'
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.6)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(59, 130, 246, 0.4)';
            }}
          >
            {isSubmitting ? (
              <>
                <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                {p('loading')}
              </>
            ) : (
              <>
                {p('continue')}
                <ArrowRight size={20} />
              </>
            )}
          </button>
        </form>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1rem',
          background: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '10px',
          marginBottom: '1rem',
          maxWidth: '650px',
          margin: '0 auto 1rem auto'
        }}>
          <Lock size={14} color="#10b981" />
          <span style={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '0.8rem',
            textAlign: 'center'
          }}>
            {p('security_notice')}
          </span>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-around',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          {features.map((feature, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '0.8rem'
            }}>
              <CheckCircle size={16} color="#10b981" />
              <span>{feature}</span>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
        input::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }
      `}</style>
    </div>
  );
};

export default DemoEntry;
